import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";

type Profile = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  is_admin: boolean | null;
  onboarding_completed: boolean | null;
  favourite_team: string | null;
  team_colour: string | null;
  avatar_icon: string | null;
  created_at: string | null;
  email_notifications_enabled: boolean;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {},
  refetchProfile: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string, email?: string | null) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, display_name, avatar_url, is_admin, onboarding_completed, favourite_team, team_colour, avatar_icon, created_at, email_notifications_enabled")
      .eq("id", userId)
      .single();
    setProfile(data);
    if (data) {
      analytics.identify(userId, {
        display_name: data.display_name,
        favourite_team: data.favourite_team,
        avatar_icon: data.avatar_icon,
        is_admin: data.is_admin,
        ...(email ? { $email: email } : {}),
      });
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        if (session?.user) {
          setTimeout(() => fetchProfile(session.user.id, session.user.email), 0);
        } else {
          setProfile(null);
          analytics.reset();
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        await fetchProfile(session.user.id, session.user.email);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    analytics.track("sign_out");
    analytics.reset();
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  };

  const refetchProfile = async () => {
    if (session?.user) {
      await fetchProfile(session.user.id, session.user.email);
    }
  };

  return (
    <AuthContext.Provider
      value={{ session, user: session?.user ?? null, profile, loading, signOut, refetchProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
